import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    CButton, CCard, CCardBody, CCardHeader, CForm, CCardFooter, CFormInput
} from '@coreui/react';

import { apiClient } from 'services/Api';
import { jwtDecode } from 'jwt-decode';
import { triggerAlert } from 'components/globals/AlertNotification';
import ToastNotification from 'components/globals/ToastNotification';
import TextEditor from 'components/forms/TextEditor';

const EventReport = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);
    const location = useLocation();
    const [toast, setToast] = useState(null);
    const [isEditing, setIsEditing] = useState(true);

    const token = localStorage.getItem("access_token");
    const decodedToken = jwtDecode(token);
    const is_director = decodedToken.is_director;
    const user_id = decodedToken.user_id;

    const fetchEventDetails = async () => {
        let data = null; // Declare a variable to store API response

        try {
            const response = await apiClient.get(`/events/${id}/`);
            data = response.data;
            setFormData(data);
            setIsEditing(is_director || data?.participants.includes(user_id));
        } catch (error) {
            triggerAlert('Formação/Evento não encontrada!', 'error');
            navigate("/formacoes-eventos");
        }
    };

    useEffect(() => {
        fetchEventDetails();
    }, [id, location.pathname]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let updatedValue;

        if (value === "true") {
            updatedValue = true;
        } else if (value === "false") {
            updatedValue = false;
        } else {
            updatedValue = value || null; // Convert empty values to null
        }

        setFormData((prevFormData) => {
            let updatedForm = {
                ...prevFormData,
                [name]: updatedValue,
            };

            return updatedForm;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await apiClient.put(`/events/${id}/`, formData);
            triggerAlert('Relatótio Formação/Evento atualizada com sucesso!', 'success');
            navigate('/formacoes-eventos');
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Erro ao gravar';
            setToast({ message: { errorMessage }, type: 'error' });
            console.error('Error submitting form:', error);
        }
    };

    if (formData !== null) {
        if (!isEditing) {
            return (
                <div className="container mt-4">
                    <h4 className="text-center mb-5">Relatório da Formação/Evento</h4>
                    {[
                        { label: "Designação", value: formData.designation || "-" },
                        { label: "Descrição da Formação/Evento", value: <div dangerouslySetInnerHTML={{ __html: formData.report_description || "-" }} /> },
                        { label: "Painel 1", value: <div dangerouslySetInnerHTML={{ __html: formData.panel1 || "-" }} /> },
                        { label: "Painel 2", value: <div dangerouslySetInnerHTML={{ __html: formData.panel2 || "-" }} /> },
                        { label: "Documentação Fornecida", value: <div dangerouslySetInnerHTML={{ __html: formData.report_docs || "-" }} /> },
                        { label: "Tópicos novos a pesquisar e/ou informação a partilhar", value: <div dangerouslySetInnerHTML={{ __html: formData.report_topics || "-" }} /> },
                        { label: "Contactos efetuados", value: <div dangerouslySetInnerHTML={{ __html: formData.report_contacts || "-" }} /> },
                    ].map((item, index) => (
                        <CCard key={index} className="mb-3">
                            <CCardHeader>
                                <h5>{item.label}</h5>
                            </CCardHeader>
                            <CCardBody>
                                <div>{item.value || ""}</div>
                            </CCardBody>
                        </CCard>
                    ))}

                    {/* Back Button */}
                    <div className="d-flex justify-content-end gap-2 mt-3 mb-3">
                        <CButton color="danger" className="text-white" onClick={() => navigate('/formacoes-eventos')}>
                            Voltar
                        </CButton>
                    </div>
                </div>
            );
        }

        return (
            <div className="container mt-4">
                {/* Error Toast - Appears on submit failure */}
                <ToastNotification message={toast?.message} type={toast?.type} visible={!!toast} />
                <CCard>
                    <CCardHeader>
                        <h3>Relatório Formação/Evento</h3>
                    </CCardHeader>
                    <CCardBody>
                        <CForm onSubmit={handleSubmit}>
                            <CFormInput
                                type="text"
                                name="designation"
                                label={<strong>Designação</strong>}
                                value={formData.designation ?? ""}
                                readonly
                                plainText
                                onChange={handleInputChange}
                                className="mb-3"
                            />
                            <TextEditor
                                label="Descrição da Formação/Evento"
                                value={formData.report_description ?? ''}
                                onChange={(value) =>
                                    handleInputChange({ target: { name: "report_description", value } })
                                }
                                className="mb-3"
                            />

                            <TextEditor
                                label="Painel 1"
                                value={formData.report_panel1 ?? ''}
                                onChange={(value) =>
                                    handleInputChange({ target: { name: "report_panel1", value } })
                                }
                                className="mb-3"
                            />

                            <TextEditor
                                label="Painel 2"
                                value={formData.report_panel2 ?? ''}
                                onChange={(value) =>
                                    handleInputChange({ target: { name: "report_panel2", value } })
                                }
                                className="mb-3"
                            />

                            <TextEditor
                                label="Documentação Fornecida"
                                value={formData.relatorio_documentacao ?? ''}
                                onChange={(value) =>
                                    handleInputChange({ target: { name: "relatorio_documentacao", value } })
                                }
                                className="mb-3"
                            />

                            <TextEditor
                                label="Tópicos novos a pesquisar e/ou informação a partilhar"
                                value={formData.relatorio_topicos ?? ''}
                                onChange={(value) =>
                                    handleInputChange({ target: { name: "relatorio_topicos", value } })
                                }
                                className="mb-3"
                            />

                            <TextEditor
                                label="Contactos efetuados"
                                value={formData.relatorio_contactos ?? ''}
                                onChange={(value) =>
                                    handleInputChange({ target: { name: "relatorio_contactos", value } })
                                }
                                className="mb-3"
                            />
                        </CForm>
                    </CCardBody>
                    <CCardFooter className="d-flex justify-content-end gap-2">
                        <CButton color="danger" className="text-white" onClick={() => navigate("/formacoes-eventos")}>
                            Cancelar
                        </CButton>
                        <CButton color="success" className="text-white" type="submit" onClick={handleSubmit}>
                            Confirmar
                        </CButton>
                    </CCardFooter>
                </CCard>
            </div>
        );
    }
};

export default EventReport;